import type { CONTRACT_TYPES_RATIFIED, FieldApplicabilityByTypeRow } from '@clm/shared-types';

/**
 * The pure half of the applicability-by-type section of Admin **Extraction
 * accuracy** — CLM-133. Kept apart from `FieldAccuracy.tsx` for the same
 * reason `field-accuracy-rows.ts` is: a component module that also exports
 * functions breaks fast refresh.
 */

/** What an unmeasured cell reads as — never a percentage (measured_contracts = 0). */
export const NOT_MEASURED_COPY = 'not yet measured';

/** One field, pivoted so each ratified contract type is its own column. */
export interface ApplicabilityFieldRow {
  readonly field_name: string;
  readonly is_high_stakes: boolean;
  readonly byType: Readonly<Partial<Record<(typeof CONTRACT_TYPES_RATIFIED)[number], FieldApplicabilityByTypeRow>>>;
}

/**
 * Flat `(field, contract_type)` rows from the wire, pivoted to one row per
 * field with up to three type columns. A `(field, type)` pair absent from the
 * response (that type has zero contracts in the caller's scope) is left
 * `undefined` in `byType` — the component reads that as "no contracts of this
 * type in your scope", never as "not yet measured" (which is
 * `measured_contracts = 0` on a row that IS present).
 */
export function pivotApplicabilityRows(rows: readonly FieldApplicabilityByTypeRow[]): readonly ApplicabilityFieldRow[] {
  const byField = new Map<string, { is_high_stakes: boolean; byType: Map<string, FieldApplicabilityByTypeRow> }>();
  for (const row of rows) {
    let entry = byField.get(row.field_name);
    if (entry === undefined) {
      entry = { is_high_stakes: row.is_high_stakes, byType: new Map() };
      byField.set(row.field_name, entry);
    }
    entry.byType.set(row.contract_type, row);
  }
  return [...byField.entries()]
    .map(
      ([field_name, entry]): ApplicabilityFieldRow => ({
        field_name,
        is_high_stakes: entry.is_high_stakes,
        byType: Object.fromEntries(entry.byType),
      }),
    )
    .sort((a, b) => (a.field_name < b.field_name ? -1 : a.field_name > b.field_name ? 1 : 0));
}
